import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import { pluralise } from '@/lib/format';
import type { DealDeliverableLink, DealProduct } from '@/lib/types';
import {
  Button,
  Checkbox,
  Modal,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/**
 * Turns a deal's lines into STANDALONE client deliverables, one per ticked line.
 *
 * ⛔ **Nothing here is typed.** Product, project, promised (the line's quantity),
 * cities and regions are all derived by the server from the line, so the dialog
 * shows them read-only and sends line ids and nothing else.
 *
 * ⚠️ **A partial outcome is ordinary.** Every requested line comes back either
 * created or skipped with the server's own sentence, so the report is per line
 * and never "nothing was saved" over a request that created some.
 *
 * ⚠️ **A link already on the tracker is decided by `links`, never by the
 * result of this session alone** — the page re-reads links after a save, so a
 * line added from another tab reads as already added too. When `links` is
 * `null` (the read failed) the dialog offers every line and the server's
 * `already-added` refusal speaks for itself.
 */

export interface AddToDeliverablesDialogProps {
  onClose: () => void;
  lines: readonly DealProduct[];
  /** False when the deal names no company: every line would be skipped `no-company`. */
  hasCompany: boolean;
  /** `null` = not read, or the read failed. Never treat as "none added". */
  links: readonly DealDeliverableLink[] | null;
  /** Re-reads the links on the page after a save. */
  onSaved: () => void;
}

type LineOutcome = { kind: 'created' } | { kind: 'skipped'; message: string };

const LIST_SEPARATOR = ', ';

function cityText(line: DealProduct): string {
  return line.cities.length === 0
    ? 'No cities'
    : line.cities.map((city) => city.name).join(LIST_SEPARATOR);
}

function regionText(line: DealProduct): string {
  return line.regions.length === 0
    ? 'No regions'
    : line.regions.map((region) => region.name).join(LIST_SEPARATOR);
}

function projectText(line: DealProduct): string {
  return line.project?.name ?? 'No project';
}

export function AddToDeliverablesDialog({
  onClose,
  lines,
  hasCompany,
  links,
  onSaved,
}: AddToDeliverablesDialogProps) {
  const linkByLine = useMemo(() => {
    const map = new Map<number, DealDeliverableLink>();
    for (const link of links ?? []) map.set(link.dealProductId, link);
    return map;
  }, [links]);

  const [outcomes, setOutcomes] = useState<Map<number, LineOutcome>>(() => new Map());
  /* Ticked by default: every line not already backing a deliverable. Seeded
     once at mount — the page mounts this dialog afresh on every open. */
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(lines.filter((line) => !linkByLine.has(line.id)).map((line) => line.id)),
  );
  const [saving, setSaving] = useState(false);
  /* State batches, so two clicks in one tick would both read `saving === false`. */
  const savingRef = useRef(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  /** A line can be ticked only if it is not on the tracker and has no outcome yet. */
  const isOpen = (line: DealProduct) =>
    hasCompany && !linkByLine.has(line.id) && !outcomes.has(line.id);

  const toSend = lines.filter((line) => isOpen(line) && selected.has(line.id)).map((l) => l.id);
  const createdCount = [...outcomes.values()].filter((o) => o.kind === 'created').length;
  const skippedCount = outcomes.size - createdCount;
  const anyOpen = lines.some(isOpen);

  const toggle = (lineId: number, on: boolean) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (on) next.add(lineId);
      else next.delete(lineId);
      return next;
    });
  };

  const save = () => {
    if (savingRef.current || toSend.length === 0) return;
    savingRef.current = true;
    setSaving(true);
    setRequestError(null);
    const requested = toSend;
    api
      .createDeliverablesFromDealLines(requested)
      .then((result) => {
        const skippedById = new Map(
          result.data.skipped.map((entry) => [entry.dealProductId, entry.message]),
        );
        setOutcomes((previous) => {
          const next = new Map(previous);
          for (const lineId of requested) {
            const message = skippedById.get(lineId);
            next.set(lineId, message === undefined ? { kind: 'created' } : { kind: 'skipped', message });
          }
          return next;
        });
      })
      .catch((caught: unknown) => {
        setRequestError(
          caught instanceof Error ? caught.message : 'The request to add the deliverables failed.',
        );
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
        /* Re-read either way: a failed response is not evidence nothing landed. */
        onSaved();
      });
  };

  const statusOf = (line: DealProduct) => {
    const outcome = outcomes.get(line.id);
    if (outcome?.kind === 'created') return <span className="text-success">Added</span>;
    if (outcome?.kind === 'skipped') return <span className="text-danger">{outcome.message}</span>;
    const link = linkByLine.get(line.id);
    if (link) {
      return (
        <span className="text-fg-muted">
          {link.removed ? 'Already a deliverable · removed from tracker' : 'Already a deliverable'}
        </span>
      );
    }
    return null;
  };

  const tickFor = (line: DealProduct, label: ReactNode) =>
    isOpen(line) ? (
      <Checkbox
        label={label}
        checked={selected.has(line.id)}
        disabled={saving}
        onChange={(event) => toggle(line.id, event.target.checked)}
      />
    ) : null;

  const saveLabel =
    toSend.length === 0
      ? 'Add deliverables'
      : `Add ${pluralise(toSend.length, 'deliverable')}`;

  return (
    <Modal
      open
      onClose={onClose}
      title="Add deal lines to deliverables"
      size="xl"
      dismissOnBackdrop={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {outcomes.size > 0 ? 'Close' : 'Cancel'}
          </Button>
          {hasCompany && anyOpen && (
            <Button
              variant="primary"
              onClick={save}
              loading={saving}
              disabled={toSend.length === 0}
            >
              {saveLabel}
            </Button>
          )}
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-3">
        {!hasCompany && (
          <Notice tone="danger" role="alert" title="This deal names no company">
            A deliverable belongs to a company, so these lines cannot be added until the deal
            has one.
          </Notice>
        )}
        {links === null && hasCompany && (
          <Notice tone="warning">
            Which lines are already deliverables could not be checked.
          </Notice>
        )}
        {requestError && (
          <Notice tone="danger" role="alert" title="The request failed">
            {requestError}
          </Notice>
        )}
        {createdCount > 0 && (
          <Notice
            tone="success"
            role="status"
            title={`${pluralise(createdCount, 'deliverable')} added`}
            action={<TextLink to="/client-deliverables">Open the tracker</TextLink>}
          >
            {skippedCount > 0 ? `${pluralise(skippedCount, 'line')} could not be added.` : null}
          </Notice>
        )}
        {createdCount === 0 && skippedCount > 0 && (
          <Notice tone="warning" role="status">
            {`${pluralise(skippedCount, 'line')} could not be added.`}
          </Notice>
        )}

        {/* Desktop and tablet: a table. */}
        <div className="hidden min-w-0 sm:block">
          <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
            <Table className="min-w-[760px]">
              <THead>
                <TR>
                  <TH className="w-10">
                    <span className="sr-only">Add</span>
                  </TH>
                  <TH>Product</TH>
                  <TH>Project</TH>
                  <TH numeric>Promised</TH>
                  <TH>Cities</TH>
                  <TH>Regions</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <TBody>
                {lines.map((line) => (
                  <TR key={line.id}>
                    <TD>
                      {tickFor(
                        line,
                        <span className="sr-only">Add {line.product.productName}</span>,
                      )}
                    </TD>
                    <TD className="font-medium text-fg">{line.product.productName}</TD>
                    <TD>{projectText(line)}</TD>
                    <TD numeric>{line.quantity}</TD>
                    <TD>{cityText(line)}</TD>
                    <TD>{regionText(line)}</TD>
                    <TD className="text-xs">{statusOf(line)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>

        {/* Phone: one stacked card per line. */}
        <ul className="flex min-w-0 flex-col gap-2 sm:hidden">
          {lines.map((line) => {
            const status = statusOf(line);
            return (
              <li key={line.id} className="min-w-0 rounded-md border border-border p-3 text-sm">
                {tickFor(line, <span className="font-medium text-fg">{line.product.productName}</span>) ?? (
                  <div className="font-medium break-words text-fg">{line.product.productName}</div>
                )}
                <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                  <dt className="text-fg-muted">Project</dt>
                  <dd className="break-words text-fg-secondary">{projectText(line)}</dd>
                  <dt className="text-fg-muted">Promised</dt>
                  <dd className="text-fg-secondary tabular-nums">{line.quantity}</dd>
                  <dt className="text-fg-muted">Cities</dt>
                  <dd className="break-words text-fg-secondary">{cityText(line)}</dd>
                  <dt className="text-fg-muted">Regions</dt>
                  <dd className="break-words text-fg-secondary">{regionText(line)}</dd>
                </dl>
                {status && <div className="mt-2 text-xs break-words">{status}</div>}
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
